import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal, Plus, X, TrendingUp, Building2, Calendar, Users, ChevronRight, Star, Sparkles, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { mockConnections, mockCompanies, mockEvents } from '../../data/mock';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const RightSidebar = () => {
  const [dismissedSuggestions, setDismissedSuggestions] = useState([]);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);

  const handleDismissSuggestion = (personId) => {
    setDismissedSuggestions([...dismissedSuggestions, personId]);
  };

  const handleConnectClick = (person) => {
    setSelectedPerson(person);
    setConnectionMessage('');
    setShowConnectModal(true);
  };

  const handleSendRequest = async () => {
    if (!selectedPerson) return;
    
    try {
      setSendingRequest(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_BASE_URL}/api/connections/request`,
        {
          receiver_id: selectedPerson.id,
          message: connectionMessage.trim() || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Success - close modal and dismiss suggestion
      setShowConnectModal(false);
      handleDismissSuggestion(selectedPerson.id);
      
      // Show success message (you can add a toast notification here)
      alert('Connection request sent successfully!');
      
    } catch (error) {
      console.error('Error sending connection request:', error);
      alert(error.response?.data?.detail || 'Failed to send connection request');
    } finally {
      setSendingRequest(false);
    }
  };

  const activePeople = mockConnections.filter(c => !c.isConnected && !dismissedSuggestions.includes(c.id));

  return (
    <div className="space-y-4 hide-mobile">
      {/* People You May Know */}
      <Card className="card-hover shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center">
              <Users className="w-4 h-4 mr-2 text-blue-600" />
              People you may know
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-gray-600 transition-colors duration-200">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-4">
            {activePeople.slice(0, 3).map((person) => (
              <div key={person.id} className="flex items-start space-x-3 group animate-in slide-in-from-right-2 duration-300">
                <Avatar className="w-12 h-12 avatar-hover ring-2 ring-transparent group-hover:ring-blue-200 transition-all duration-300">
                  <AvatarImage src={person.avatar} alt={person.name} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold">
                    {person.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm truncate group-hover:text-blue-600 transition-colors duration-200 cursor-pointer">
                    {person.name}
                  </h4>
                  <p className="text-xs text-gray-600 truncate mb-1">{person.title}</p>
                  <p className="text-xs text-gray-500 mb-3 flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    {person.mutualConnections} mutual connections
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs h-7 flex-1 btn-hover border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200"
                      onClick={() => handleConnectClick(person)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Connect
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-xs h-7 px-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
                      onClick={() => handleDismissSuggestion(person.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Separator className="my-3" />
          <Link to="/network">
            <Button variant="ghost" className="w-full text-xs h-auto p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 font-medium transition-all duration-200 group">
              Show all recommendations
              <ChevronRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform duration-200" />
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Training Industry News */}
      <Card className="card-hover shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center">
              <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
              Training Industry News
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-gray-600 transition-colors duration-200">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-1">
          <div className="space-y-3">
            <Link to="/news/ai-corporate-training" className="block cursor-pointer hover:bg-blue-50 p-3 rounded-lg sidebar-item group transition-all duration-200">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 animate-pulse"></div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm mb-1 group-hover:text-blue-700 transition-colors duration-200 line-clamp-2">
                    AI in Corporate Training reaches new heights
                  </h4>
                  <div className="flex items-center text-xs text-gray-600 space-x-2">
                    <span className="font-medium">2,450 readers</span>
                    <span>•</span>
                    <span>4h ago</span>
                    <Badge variant="secondary" className="ml-auto bg-red-100 text-red-700 text-xs">Hot</Badge>
                  </div>
                </div>
              </div>
            </Link>
            
            <Link to="/news/vr-training" className="block cursor-pointer hover:bg-blue-50 p-3 rounded-lg sidebar-item group transition-all duration-200">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm mb-1 group-hover:text-blue-700 transition-colors duration-200 line-clamp-2">
                    Virtual Reality training adoption grows 300%
                  </h4>
                  <div className="flex items-center text-xs text-gray-600 space-x-2">
                    <span>1,890 readers</span>
                    <span>•</span>
                    <span>8h ago</span>
                  </div>
                </div>
              </div>
            </Link>
            
            <Link to="/news/microlearning-investment" className="block cursor-pointer hover:bg-blue-50 p-3 rounded-lg sidebar-item group transition-all duration-200">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm mb-1 group-hover:text-blue-700 transition-colors duration-200 line-clamp-2">
                    Microlearning platforms see record investment
                  </h4>
                  <div className="flex items-center text-xs text-gray-600 space-x-2">
                    <span>3,120 readers</span>
                    <span>•</span>
                    <span>12h ago</span>
                    <Badge variant="secondary" className="ml-auto bg-orange-100 text-orange-700 text-xs">Trending</Badge>
                  </div>
                </div>
              </div>
            </Link>
            
            <Link to="/news/skills-hiring" className="block cursor-pointer hover:bg-blue-50 p-3 rounded-lg sidebar-item group transition-all duration-200">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm mb-1 group-hover:text-blue-700 transition-colors duration-200 line-clamp-2">
                    Skills-based hiring transforms L&D strategies
                  </h4>
                  <div className="flex items-center text-xs text-gray-600 space-x-2">
                    <span>2,678 readers</span>
                    <span>•</span>
                    <span>1d ago</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
          <Separator className="my-3" />
          <Link to="/news">
            <Button variant="ghost" className="w-full text-xs h-auto p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 font-medium transition-all duration-200 group">
              Show more news
              <ChevronRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform duration-200" />
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Featured Companies */}
      <Card className="card-hover shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center">
              <Building2 className="w-4 h-4 mr-2 text-purple-600" />
              Companies to follow
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-gray-600 transition-colors duration-200">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-4">
            {mockCompanies.slice(0, 2).map((company) => (
              <div key={company.id} className="flex items-start space-x-3 group">
                <Link to={`/company/${company.id}`}>
                  <Avatar className="w-12 h-12 avatar-hover ring-2 ring-transparent group-hover:ring-purple-200 transition-all duration-300 cursor-pointer">
                    <AvatarImage src={company.logo} alt={company.name} />
                    <AvatarFallback className="bg-gradient-to-br from-purple-500 to-purple-600 text-white font-semibold">
                      {company.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/company/${company.id}`}>
                    <h4 className="font-semibold text-sm truncate group-hover:text-purple-600 transition-colors duration-200 cursor-pointer">
                      {company.name}
                    </h4>
                  </Link>
                  <p className="text-xs text-gray-600 truncate mb-1">{company.industry}</p>
                  <div className="flex items-center text-xs text-gray-500 mb-3">
                    <Users className="w-3 h-3 mr-1" />
                    <span>{company.followers.toLocaleString()} followers</span>
                    <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700 text-xs">Growing</Badge>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-xs h-7 btn-hover border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-all duration-200"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Follow
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Events */}
      <Card className="card-hover shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-orange-600" />
              Training Events
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-gray-600 transition-colors duration-200">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {mockEvents.slice(0, 2).map((event) => (
              <Link key={event.id} to={`/events/${event.id}`} className="block cursor-pointer hover:bg-orange-50 p-3 rounded-lg sidebar-item group transition-all duration-200">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:shadow-soft transition-all duration-200">
                    <Calendar className="w-4 h-4 text-orange-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate mb-1 group-hover:text-orange-700 transition-colors duration-200">
                      {event.title}
                    </h4>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {event.date} • {event.time}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-gray-500">
                          <Users className="w-3 h-3 mr-1" />
                          {event.attendees} attending
                        </div>
                        {event.id === '1' && (
                          <Badge variant="secondary" className="bg-orange-100 text-orange-700 text-xs">
                            <Star className="w-2 h-2 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <Separator className="my-3" />
          <Button variant="ghost" className="w-full text-xs h-auto p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 font-medium transition-all duration-200 group">
            <Link to="/events" className="flex items-center w-full">
              View all events
              <ChevronRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform duration-200" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Premium Promotion */}
      <Card className="card-hover border-2 border-gradient-to-r from-orange-200 to-red-200 bg-gradient-to-br from-orange-50 via-red-50 to-orange-50 relative overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300">
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-400/10 to-red-400/10 rounded-full -mr-10 -mt-10"></div>
        <div className="absolute top-2 right-2">
          <Sparkles className="w-4 h-4 text-orange-500 animate-pulse" />
        </div>
        <CardContent className="p-4 relative">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center shadow-soft">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <h4 className="font-semibold text-sm mb-2 text-gray-800">Advanced Training Certification</h4>
            <p className="text-xs text-gray-600 mb-4 leading-relaxed">
              Master the latest training methodologies with our comprehensive certification program. Join 10,000+ professionals.
            </p>
            <div className="space-y-2">
              <Button size="sm" className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-xs shadow-soft btn-hover border-none">
                Start Free Trial
              </Button>
              <p className="text-xs text-gray-500">No credit card required</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="absolute top-2 right-8 text-gray-400 hover:text-gray-600 w-6 h-6 p-0 transition-colors duration-200"
          >
            <X className="w-3 h-3" />
          </Button>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="px-2 py-4 text-center">
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs text-gray-500 mb-3">
          <a href="#" className="hover:text-blue-600 transition-colors duration-200">About</a>
          <a href="#" className="hover:text-blue-600 transition-colors duration-200">Privacy</a>
          <a href="#" className="hover:text-blue-600 transition-colors duration-200">Terms</a>
          <a href="#" className="hover:text-blue-600 transition-colors duration-200">Help</a>
        </div>
        <p className="text-xs text-gray-400">TrainerLinked Corporation © 2024</p>
      </div>

      {/* Connection Request Modal */}
      <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Connect with {selectedPerson?.name}</DialogTitle>
            <DialogDescription>
              Add a note to personalize your invitation (optional)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Avatar className="w-12 h-12">
                <AvatarImage src={selectedPerson?.avatar} alt={selectedPerson?.name} />
                <AvatarFallback className="bg-blue-600 text-white">
                  {selectedPerson?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold">{selectedPerson?.name}</p>
                <p className="text-sm text-gray-600">{selectedPerson?.title}</p>
              </div>
            </div>

            <div>
              <Textarea
                placeholder="Hi, I'd like to connect with you on TrainerLinked.in..."
                value={connectionMessage}
                onChange={(e) => setConnectionMessage(e.target.value)}
                maxLength={300}
                rows={4}
                className="resize-none"
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-gray-500">
                  {connectionMessage.length}/300 characters
                </p>
                {connectionMessage.length >= 280 && (
                  <p className="text-xs text-orange-500">
                    {300 - connectionMessage.length} characters remaining
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowConnectModal(false)}
                disabled={sendingRequest}
              >
                Cancel
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleSendRequest}
                disabled={sendingRequest}
              >
                {sendingRequest ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RightSidebar;